import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["downloadButton", "statusMessage", "spinner"]
  static values = { 
    quoteId: String,
    pollInterval: { type: Number, default: 2000 },
    csrfToken: String
  }
  
  connect() {
    // Get CSRF token if not provided
    if (!this.csrfTokenValue) {
      const tokenElement = document.querySelector('[name="csrf-token"]')
      if (tokenElement) {
        this.csrfTokenValue = tokenElement.content
      }
    }
  }
  
  async download(event) {
    event.preventDefault()
    
    // Disable button and show loading state
    this.setLoadingState(true)
    this.showStatus("Generating report...", "info")
    
    try {
      const response = await fetch(`/oms/quotes/${this.quoteIdValue}/reports`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfTokenValue,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          title: `Quote ${this.quoteIdValue} Report`,
          include_terms: false,
          include_documents: true
        })
      })
      
      const data = await response.json()
      
      if (response.ok) {
        this.pollForCompletion(data.status_url)
      } else {
        throw new Error(data.error || data.errors?.join(', ') || "Failed to generate report")
      }
    } catch (error) {
      this.showError(error.message || "Network error occurred")
      this.setLoadingState(false)
    }
  }
  
  async pollForCompletion(statusUrl) {
    const checkStatus = async () => {
      try {
        const response = await fetch(statusUrl, {
          headers: {
            'Accept': 'application/json'
          }
        })
        const data = await response.json()
        
        switch(data.status) {
          case 'completed':
            this.showStatus("Report ready! Opening...", "success")
            // Open PDF in new tab for viewing
            window.open(data.view_url, '_blank')
            this.setLoadingState(false)
            
            // Show download link
            setTimeout(() => {
              this.showStatus("Report opened in new tab", "success")
            }, 1000)
            break
            
          case 'failed':
            throw new Error(data.error || "Report generation failed")
            
          case 'generating':
            // Update status message periodically
            const dots = this.statusMessageTarget.textContent.match(/\./g)?.length || 0
            const newDots = dots >= 3 ? 0 : dots + 1
            this.showStatus("Generating report" + ".".repeat(newDots), "info")
            
            // Continue polling
            setTimeout(checkStatus, this.pollIntervalValue)
            break
            
          default:
            // Pending or unknown status, continue polling
            setTimeout(checkStatus, this.pollIntervalValue)
        }
      } catch (error) {
        this.showError(error.message || "Failed to check report status")
        this.setLoadingState(false)
      }
    }
    
    // Start polling
    checkStatus()
  }
  
  setLoadingState(loading) {
    if (this.hasDownloadButtonTarget) {
      this.downloadButtonTarget.disabled = loading
      
      // Toggle button content if it has both text and spinner
      if (this.hasSpinnerTarget) {
        if (loading) {
          this.spinnerTarget.classList.remove('hidden')
        } else {
          this.spinnerTarget.classList.add('hidden')
        }
      }
    }
  }
  
  showStatus(message, type = "info") {
    if (!this.hasStatusMessageTarget) return
    
    this.statusMessageTarget.textContent = message
    this.statusMessageTarget.classList.remove('hidden', 'text-red-600', 'text-green-600', 'text-gray-600')
    
    switch(type) {
      case 'error':
        this.statusMessageTarget.classList.add('text-red-600')
        break
      case 'success':
        this.statusMessageTarget.classList.add('text-green-600')
        break
      default:
        this.statusMessageTarget.classList.add('text-gray-600')
    }
  }
  
  showError(message) {
    this.showStatus(message, 'error')
    
    // Auto-hide error after 5 seconds
    setTimeout(() => {
      if (this.hasStatusMessageTarget && this.statusMessageTarget.textContent === message) {
        this.statusMessageTarget.classList.add('hidden')
      }
    }, 5000)
  }
}
